import { useCallback } from "react";
import { FeaturePromotion } from "../types/FeaturePromotion";
import { HttpError } from "../types/Error";
import useApiClient from "./ApiClient";

/** Throws the API's own message, so callers can show why a save was refused. */
function unwrap<T>(result: T | HttpError): T {
  if (result && (result as HttpError).errorCode) {
    throw new Error((result as HttpError).description || "An error occurred");
  }
  return result as T;
}

const useFeaturePromotionsService = () => {
  const { get, post, put, delete: del } = useApiClient();

  const getFeaturePromotions = useCallback(
    async (includeEnded: boolean): Promise<FeaturePromotion[]> => {
      const result = await get<FeaturePromotion[]>(
        `/admin/featurepromotions?includeEnded=${includeEnded}`
      );
      return unwrap(result) ?? [];
    },
    [get]
  );

  const getFeaturePromotion = useCallback(
    async (id: string): Promise<FeaturePromotion> => {
      const result = await get<FeaturePromotion>(
        `/admin/featurepromotions/${encodeURIComponent(id)}`
      );
      return unwrap(result);
    },
    [get]
  );

  /** Creates when the promotion has no id, otherwise replaces it. */
  const saveFeaturePromotion = useCallback(
    async (promotion: FeaturePromotion): Promise<FeaturePromotion> => {
      const result = promotion.id
        ? await put<FeaturePromotion>(
            `/admin/featurepromotions/${encodeURIComponent(promotion.id)}`,
            promotion
          )
        : await post<FeaturePromotion>("/admin/featurepromotions", promotion);
      return unwrap(result);
    },
    [post, put]
  );

  const deleteFeaturePromotion = useCallback(
    async (id: string): Promise<void> => {
      const result = await del(`/admin/featurepromotions/${encodeURIComponent(id)}`);
      unwrap(result);
    },
    [del]
  );

  return {
    getFeaturePromotions,
    getFeaturePromotion,
    saveFeaturePromotion,
    deleteFeaturePromotion,
  };
};

export default useFeaturePromotionsService;
